import { SupportedDashboardComponent } from "@aidkitorg/types/lib/admin-panel/dashboard";
import { StagedComponent } from "@aidkitorg/types/lib/admin-panel/dashboard";
import { useDashboards, distroComponentToAdminPanel } from "../../../Contexts/DashboardsContext";

// Grid ids encode the tree path: `${componentId}-${childIdx}-...`, so the
// trailing numeric segments are the index path into config.components.
// Match on the `-` boundary explicitly (not startsWith) so `${dashboardId}-1`
// doesn't falsely prefix-match `${dashboardId}-10-0` at index 10+.
function idToIndexPath(componentId: string, id: string): number[] | null {
  if (id === componentId) return [];
  const prefix = `${componentId}-`;
  if (!id.startsWith(prefix)) return null;
  const segments = id.slice(prefix.length).split('-');
  // Per-segment digit check: Number('') is 0, so a trailing/double dash would otherwise map to index 0.
  if (!segments.every((s) => /^\d+$/.test(s))) return null;
  return segments.map((s) => Number(s));
}

function nodeAtPath(config: any, path: number[]): any {
  let node: any = config;
  for (const i of path) node = node?.components?.[i];
  return node;
}

// Expanded Subsurvey Summary children are display-only and must never be written back to Distro.
function isExpandedSubsurveySummary(node: any): boolean {
  return node?._expandedFrom === 'Subsurvey Summary';
}

export function findStagedConfigById(
  stagedComponents: Array<StagedComponent | { id: string; config: any }>,
  id: string,
): any | undefined {
  for (const component of stagedComponents) {
    const path = idToIndexPath(component.id, id);
    if (!path) continue;
    const node = nodeAtPath(component.config, path);
    // Nested staged config holds raw distro children (top-level is pre-adapted
    // at load), so normalize to the admin-panel shape the card expects.
    if (node) return distroComponentToAdminPanel(node);
  }
  return undefined;
}

function replaceAtPath(config: any, path: number[], updated: SupportedDashboardComponent): any {
  if (path.length === 0) return JSON.parse(JSON.stringify(updated));
  const [i, ...rest] = path;
  // Mirror findStagedConfigById's defensive read so a stale path bails instead of throwing.
  if (!config?.components?.[i]) return config;
  const components = config.components.slice();
  components[i] = replaceAtPath(components[i], rest, updated);
  return { ...config, components };
}

export const useUpdateComponentById = () => {
  const { setStagedComponents } = useDashboards();

  const updateComponentById = (id: string, updatedComponent: SupportedDashboardComponent) => {
    setStagedComponents((prevComponents) =>
      prevComponents.map((component) => {
        const path = idToIndexPath(component.id, id);
        if (!path) return component;
        if (isExpandedSubsurveySummary(nodeAtPath(component.config, path))) return component;
        return { ...component, config: replaceAtPath(component.config, path, updatedComponent) };
      })
    );
  };

  return updateComponentById;
};

// True when the id resolves to a widget nested inside a Dashboard Section (not a
// top-level staged component). Used to hide delete, which the flat filter can't handle.
export function isNestedComponentId(
  stagedComponents: Array<StagedComponent | { id: string; config: any }>,
  id: string,
): boolean {
  return stagedComponents.some((component) => {
    const path = idToIndexPath(component.id, id);
    return path !== null && path.length > 0;
  });
}
